/**
 * Auth / session store.
 *
 * Single source of truth for "who is logged in and is the session usable
 * right now." Wraps SuperTokens so call sites stop importing `Session.*`
 * directly and we can swap implementations later (or mock in tests).
 *
 * Lifecycle: call `init()` once after Pinia is mounted (DashboardView does
 * this on mount). After resolution, `ready` is true and the rest of the
 * fields reflect whatever state SuperTokens reported.
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import Session from 'supertokens-web-js/recipe/session'
import router from '@/router'
import { usePostHog, identifyCurrentUser } from '@/composables/usePostHog'
import { clearAuthenticatedUser as aiClearAuthenticatedUser } from '@/services/telemetryService'
import {
  hasEntraSession,
  getEntraUser,
  getEntraIdToken,
  getEntraSessionExpiresAt,
  getLastEntraWipeReason,
  signOutEntra,
  clearLocalEntraSession,
} from '@/services/entraAuthService'
import { reportSessionWipe, type SessionWipeReason } from '@/utils/sessionWipeReason'

export type AuthProvider = 'supertokens' | 'entra'

export const useAuthStore = defineStore('auth', () => {
  const userId = ref<string | null>(null)
  const isAuthenticated = ref(false)
  const ready = ref(false)
  const initializing = ref(false)
  // Which credential source is currently in effect. Drives signOut
  // behavior — SuperTokens users sign out locally, Entra users get
  // bounced through Microsoft's logout endpoint so the SSO cookie is
  // invalidated on shared devices.
  const provider = ref<AuthProvider | null>(null)
  let inflight: Promise<void> | null = null

  // Hard timeout for SuperTokens / Entra lookups. The SDKs have no built-in
  // timeout — when the auth backend is slow or unreachable, the probes can
  // sit for 30s+ while the router guard awaits, and the SPA shows a blank
  // page with no escape. Treating the timeout as "session unverifiable
  // right now" routes the user to /login (recoverable) instead of waiting.
  //
  // Azure Container Apps scale-to-zero cold starts can take 3–5s on UAT /
  // dev. 5000ms rides through a cold start without bouncing the user to
  // /login; healthy backends still resolve in <200ms so the timeout is
  // effectively invisible in the happy path. Prod can opt back into the
  // tighter 1500ms via min replicas ≥ 1 + a future env override.
  const SESSION_PROBE_TIMEOUT_MS = 5000
  // Counter for consecutive Entra refresh timeouts. Cleared on success or
  // explicit signout; survives a single reload via sessionStorage so the
  // dead-session detection works across the watchdog reload cycle.
  const ENTRA_TIMEOUT_KEY = 'eh:entra-consecutive-timeouts'

  // Returns { value, timedOut }. The legacy implementation logged "timed
  // out" whenever the setTimeout fired, even if the real promise had
  // already resolved — misleading in dev console + obscured the real
  // pattern. Track which side won the race and only warn on real timeout.
  function withTimeout<T>(p: Promise<T>, label: string): Promise<{ value: T | null; timedOut: boolean }> {
    let timer: ReturnType<typeof setTimeout> | null = null
    let timedOut = false
    return new Promise((resolve) => {
      timer = setTimeout(() => {
        timedOut = true
        console.warn(`[auth.init] ${label} timed out after ${SESSION_PROBE_TIMEOUT_MS}ms — treating as session unverifiable this boot (local state preserved)`)
        resolve({ value: null, timedOut: true })
      }, SESSION_PROBE_TIMEOUT_MS)
      p.then(
        (value) => {
          if (timedOut) return
          if (timer) clearTimeout(timer)
          resolve({ value, timedOut: false })
        },
        (err) => {
          if (timedOut) return
          if (timer) clearTimeout(timer)
          console.warn(`[auth.init] ${label} threw:`, err)
          resolve({ value: null, timedOut: false })
        },
      )
    })
  }

  async function init(): Promise<void> {
    if (inflight) return inflight
    if (ready.value) return
    initializing.value = true
    inflight = (async () => {
      try {
        // Entra wins when active — backend treats both issuers as valid,
        // but we shouldn't ask the SuperTokens SDK about a session that
        // doesn't exist (avoids spurious network errors in the console
        // when only an Entra session was stored).
        if (hasEntraSession()) {
          // localStorage has Entra bytes; validate the token is still
          // refreshable via getEntraIdToken. Four outcomes:
          //   - success: mark authenticated as Entra user
          //   - timeout: keep local state on the FIRST miss (slow net
          //     shouldn't kick a real user out), but if the SAME session
          //     times out N times in a row treat it as dead and clear.
          //     Without this counter a dead session keeps coming back and
          //     the user can never escape without manual "Reset session".
          //   - null but session bytes survive: TRANSIENT refresh failure
          //     (network/429/5xx) — keep the user signed in, retry later.
          //   - null and session bytes gone: DEFINITIVE rejection (400/401
          //     or no refresh_token) — clear local state, session is dead.
          const probe = await withTimeout(getEntraIdToken(), 'getEntraIdToken')
          if (probe.value) {
            provider.value = 'entra'
            const u = getEntraUser()
            userId.value = u?.id || u?.email || null
            isAuthenticated.value = !!userId.value
            try { sessionStorage.removeItem(ENTRA_TIMEOUT_KEY) } catch { /* ignore */ }
          } else if (probe.timedOut) {
            // A timeout while the browser KNOWS it is offline says nothing
            // about the session — it's a strike against the network, not the
            // token. Counting it (load-shedding reconnects, a laptop waking
            // up) is how a valid session got two strikes and was wiped.
            const offline = typeof navigator !== 'undefined' && navigator.onLine === false
            let consecutive = 0
            if (offline) {
              console.warn('[auth.init] Entra refresh probe timed out while offline — not counted as a strike')
            } else {
              try {
                consecutive = Number(sessionStorage.getItem(ENTRA_TIMEOUT_KEY) || 0) + 1
                sessionStorage.setItem(ENTRA_TIMEOUT_KEY, String(consecutive))
              } catch { /* sessionStorage blocked — single-shot behaviour */ }
            }
            if (consecutive >= 2) {
              console.warn(`[auth.init] Entra refresh timed out ${consecutive}× in a row — treating session as dead and clearing local state`)
              clearLocalEntraSession('probe_timeout_x2')
              try { sessionStorage.removeItem(ENTRA_TIMEOUT_KEY) } catch { /* ignore */ }
            }
            isAuthenticated.value = false
            userId.value = null
            provider.value = null
          } else if (hasEntraSession()) {
            // getEntraIdToken returned null but KEPT the session bytes: a
            // TRANSIENT refresh failure (network blip / 429 / 5xx), not a
            // rejection. It only wipes the bytes on a definitive 400/401, so a
            // surviving session means the failure is transient and the stored
            // session is still valid. Keep the user signed in and let the next
            // authenticated call retry the refresh — this is what stops a brief
            // backend blip near token-expiry (e.g. dev cold-start /
            // ERR_ADDRESS_UNREACHABLE) from bouncing a valid user to /login.
            provider.value = 'entra'
            const u = getEntraUser()
            userId.value = u?.id || u?.email || null
            isAuthenticated.value = !!userId.value
            try { sessionStorage.removeItem(ENTRA_TIMEOUT_KEY) } catch { /* ignore */ }
          } else {
            // Definitive: getEntraIdToken already wiped the session (refresh
            // endpoint rejected it, or there was no refresh_token) — hence
            // hasEntraSession() is false. Provably dead. clearLocalEntraSession
            // is idempotent (and silent on an already-empty store); call it to
            // be sure the reactive + stored state agree.
            clearLocalEntraSession(getLastEntraWipeReason() ?? 'refresh_rejected')
            try { sessionStorage.removeItem(ENTRA_TIMEOUT_KEY) } catch { /* ignore */ }
            isAuthenticated.value = false
            userId.value = null
            provider.value = null
          }
        } else {
          provider.value = 'supertokens'
          const existsProbe = await withTimeout(Session.doesSessionExist(), 'doesSessionExist')
          const exists = existsProbe.value
          isAuthenticated.value = !!exists
          if (exists) {
            const userIdProbe = await withTimeout(Session.getUserId(), 'getUserId')
            userId.value = userIdProbe.value
          } else {
            userId.value = null
            provider.value = null
          }
        }
      } finally {
        ready.value = true
        initializing.value = false
        inflight = null
      }
      // Identify in PostHog if a session was resumed (returning user that
      // never traversed the login screen this load). Fire-and-forget so a
      // PostHog hiccup can't stall auth resolution.
      if (isAuthenticated.value && userId.value) {
        void identifyCurrentUser()
        // Start the background watchdog so an Entra session that
        // expires while the tab is open gets caught and surfaces as
        // a /login redirect instead of "0 data" symptoms.
        startSessionWatchdog()
      } else {
        stopSessionWatchdog()
      }
    })()
    return inflight
  }

  // ─── Session watchdog ─────────────────────────────────────────────
  //
  // Without this, an Entra session that expires while the tab is open
  // never gets noticed: `init()` only runs once on boot, so `isAuth-
  // enticated` keeps returning true. Meanwhile the cached PostgREST
  // JWT may still be accepted (until it expires too), so requests
  // "succeed" with empty / stale data — the user clicks around and
  // sees nothing, only a hard reload routes them back to /login.
  //
  // The watchdog runs while authenticated and:
  //   - polls every 60s on `setInterval`
  //   - runs immediately on `visibilitychange → visible` (the user
  //     just came back to the tab — highest-value moment to check)
  //   - reads expires_at from localStorage (no network) and only
  //     triggers a real refresh attempt when we're in the lead window
  //   - on definitive expiry: clears local state + redirects to
  //     /login?reason=<code> so LoginView can show a banner
  let watchdogTimer: ReturnType<typeof setInterval> | null = null
  let visibilityHandler: (() => void) | null = null
  const WATCHDOG_INTERVAL_MS = 60_000
  const WATCHDOG_LEAD_MS = 60_000  // attempt refresh when <60s left

  function startSessionWatchdog(): void {
    if (watchdogTimer) return
    const run = () => { void verifySession() }
    watchdogTimer = setInterval(run, WATCHDOG_INTERVAL_MS)
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') run()
    }
    document.addEventListener('visibilitychange', visibilityHandler)
  }

  function stopSessionWatchdog(): void {
    if (watchdogTimer) { clearInterval(watchdogTimer); watchdogTimer = null }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
  }

  /**
   * Cheap session liveness check. Bails out when:
   *   - we're already on /login (nothing to do),
   *   - SuperTokens is the provider (its own SDK handles refresh on
   *     access-token expiry transparently — no separate watchdog needed),
   *   - the local session is healthy (expires_at > now + lead window).
   *
   * Falls through to a silent refresh attempt only when we're in the
   * lead window or storage has been wiped (other tab signed out, manual
   * clear, etc.) — and tears the session down on a definitive failure.
   */
  async function verifySession(): Promise<void> {
    if (!isAuthenticated.value) return
    if (provider.value !== 'entra') return
    if (router.currentRoute.value.path === '/login') return

    const expiresAt = getEntraSessionExpiresAt()
    if (expiresAt == null) {
      // localStorage was wiped from under us. This tab never did it (its
      // own wipe paths stop the watchdog first), so it was another tab —
      // a sign-out, a reset, or DevTools. Say so: for months this read as
      // "expired in background" and sent every investigation after the
      // token, when the cause was a different tab's boot reset.
      expireSession('wiped_elsewhere')
      return
    }
    if (expiresAt - Date.now() > WATCHDOG_LEAD_MS) return  // healthy

    // Lead window or already past — attempt the silent refresh. Decide by
    // whether the session SURVIVED, not by the null itself: getEntraIdToken
    // keeps the bytes on a transient failure (network/429/5xx) and only wipes
    // them on a definitive 400/401. So expire only when the bytes are gone; a
    // surviving session means a transient blip and the next 60s tick retries —
    // without this guard, one unreachable-backend moment in the refresh window
    // logged a valid user out.
    const fresh = await getEntraIdToken()
    if (!fresh && !hasEntraSession()) expireSession(getLastEntraWipeReason() ?? 'refresh_rejected')
  }

  /**
   * Provider-agnostic session teardown → clean re-login. Clears local state
   * and routes to /login?reason=<code> (LoginView shows the "session ended"
   * banner for the codes in BANNER_REASONS). Used by the Entra watchdog AND by the SuperTokens
   * onHandleEvent(UNAUTHORISED) hook in main.ts — when SuperTokens' own refresh
   * fails, its session is dead too, and nothing else was catching that.
   * Idempotent: safe to call when already on /login or already cleared.
   */
  function handleSessionExpired(reason: SessionWipeReason = 'st_unauthorised'): void {
    console.warn(`[auth] session ended (${reason}) — clearing state and routing to /login`)
    // Entra wipes report themselves when the bytes go (entraAuthService).
    // Two cases arrive here with nothing left to wipe and would otherwise
    // never reach telemetry: the key already removed by another tab, and
    // the SuperTokens provider, which keeps no Entra bytes at all.
    if (reason === 'wiped_elsewhere' || provider.value !== 'entra') {
      reportSessionWipe(reason, { provider: provider.value ?? 'none' })
    }
    clearSessionLocal(reason)  // also stops the watchdog
    if (router.currentRoute.value.path !== '/login') {
      void router.replace({ path: '/login', query: { reason } })
    }
  }

  function expireSession(reason: SessionWipeReason): void {
    console.warn(`[auth] Entra session ended in background (${reason})`)
    handleSessionExpired(reason)
  }

  /**
   * Force a re-read of the session state. Use after sign-in or anywhere
   * the session might have changed under us.
   */
  async function refresh(): Promise<void> {
    ready.value = false
    inflight = null
    await init()
  }

  /**
   * Sign out via SuperTokens and clear local state. Call sites typically
   * follow this with a redirect to the login page.
   */
  async function signOut(): Promise<void> {
    // Capture provider BEFORE we start tearing down state — the analytics
    // event needs to know which path the user was on. resetIdentity and
    // aiClearAuthenticatedUser are called after the provider-specific
    // signout so distinct_ids are cleared once everything else is done.
    const activeProvider = provider.value
    try {
      const { trackEvent, resetIdentity } = usePostHog()
      trackEvent('user_logged_out', { provider: activeProvider || 'unknown' })
      if (activeProvider === 'entra') {
        // Full Entra logout: clears the local session AND redirects
        // through Microsoft so the SSO cookie is invalidated. The page
        // navigation here means anything below resetIdentity may not
        // run — that's OK, the browser tab is leaving anyway.
        resetIdentity()
        aiClearAuthenticatedUser()
        signOutEntra()
        return
      }
      // SuperTokens path
      await Session.signOut()
      resetIdentity()
      aiClearAuthenticatedUser()
    } finally {
      userId.value = null
      isAuthenticated.value = false
      provider.value = null
      // Force the next consumer to re-resolve the session.
      ready.value = false
      inflight = null
      stopSessionWatchdog()
    }
  }

  /**
   * Local-only sign-out helper. Used when a token expired or the user
   * was kicked off mid-task and we don't want to bounce them through
   * Microsoft's logout page (which would interrupt their flow). The
   * caller is responsible for routing to /login.
   */
  function clearSessionLocal(reason: SessionWipeReason = 'user_signout'): void {
    if (provider.value === 'entra') clearLocalEntraSession(reason)
    userId.value = null
    isAuthenticated.value = false
    provider.value = null
    ready.value = false
    inflight = null
    stopSessionWatchdog()
  }

  const isReady = computed(() => ready.value)

  return {
    userId,
    isAuthenticated,
    provider,
    ready,
    initializing,
    isReady,
    init,
    refresh,
    signOut,
    clearSessionLocal,
    handleSessionExpired,
  }
})
